import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import auctionService from '../services/auctionService';
import { validateVIN } from '../utils/validators';
import './ListVehicle.css';

const ListVehicle = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const [vin, setVin] = useState('');
  const [vinData, setVinData] = useState(null);
  const [parsing, setParsing] = useState(false);

  const [formData, setFormData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    mileage: '',
    color: '',
    bodyType: '',
    transmission: '',
    engine: '',
    fuelType: '',
  });

  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [condition, setCondition] = useState({
    exterior: 5,
    interior: 5,
    mechanical: 5,
    notes: '',
  });

  const [auctionDetails, setAuctionDetails] = useState({
    reservePrice: '',
    duration: '7',
    title: '',
    description: '',
  });

  const handleParseVIN = async () => {
    if (!validateVIN(vin)) {
      setError('Please enter a valid 17-character VIN');
      return;
    }

    setParsing(true);
    setError('');
    const result = await auctionService.parseVIN(vin);
    setParsing(false);

    if (result.success) {
      setVinData(result.data);
      setFormData((prev) => ({
        ...prev,
        year: result.data.year,
        make: result.data.make,
        model: result.data.model,
      }));
      setStep(2);
    } else {
      setError(result.error || 'Failed to parse VIN');
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    setError('');

    for (const file of files) {
      const result = await auctionService.uploadPhoto(file);
      if (result.success) {
        setPhotos((prev) => [...prev, result.data.photoUrl]);
        setSuccess('Photo uploaded successfully');
      } else {
        setError(result.error || 'Failed to upload photo');
      }
    }
    setUploading(false);
  };

  const handleRemovePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConditionChange = (e) => {
    const { name, value } = e.target;
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuctionChange = (e) => {
    const { name, value } = e.target;
    setAuctionDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateAuction = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!auctionDetails.reservePrice || !auctionDetails.title) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    const auctionData = {
      vehicle: {
        vin,
        ...formData,
      },
      reservePrice: parseFloat(auctionDetails.reservePrice),
      duration: parseInt(auctionDetails.duration),
      title: auctionDetails.title,
      description: auctionDetails.description,
      photos,
      condition,
    };

    const result = await auctionService.createAuction(auctionData);
    setLoading(false);

    if (result.success) {
      setSuccess('Auction created successfully!');
      setTimeout(() => {
        navigate('/auctions');
      }, 1500);
    } else {
      setError(result.error || 'Failed to create auction');
    }
  };

  return (
    <div className="container py-4">
      <div className="form-wizard">
        <div className="wizard-steps">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`step ${s <= step ? 'active' : ''}`}>
              <div className="step-number">{s}</div>
              <div className="step-label">
                {s === 1 && 'VIN'}
                {s === 2 && 'Details'}
                {s === 3 && 'Photos'}
                {s === 4 && 'Auction'}
              </div>
            </div>
          ))}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Step 1: VIN Entry */}
        {step === 1 && (
          <div className="card form-step">
            <h2>Enter Vehicle VIN</h2>
            <p className="step-subtitle">We'll automatically pull vehicle information</p>

            <div className="form-group">
              <label htmlFor="vin" className="form-label">
                VIN (17 characters)
              </label>
              <div className="input-group">
                <input
                  type="text"
                  id="vin"
                  className="form-control"
                  value={vin}
                  onChange={(e) => setVin(e.target.value.toUpperCase())}
                  placeholder="1G1FB1S51F1234567"
                  maxLength="17"
                />
                <button
                  type="button"
                  onClick={handleParseVIN}
                  disabled={parsing}
                  className="btn btn-primary"
                >
                  {parsing ? 'Parsing...' : 'Parse VIN'}
                </button>
              </div>
            </div>

            {vinData && (
              <div className="vin-result card mt-2">
                <p>✓ VIN data found</p>
                <p className="text-muted">{vinData.year} {vinData.make} {vinData.model}</p>
              </div>
            )}

            <button
              onClick={() => setStep(2)}
              disabled={!vinData}
              className="btn btn-primary mt-3"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Vehicle Details */}
        {step === 2 && (
          <div className="card form-step">
            <h2>Vehicle Details</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Year *</label>
                <input
                  type="number"
                  name="year"
                  className="form-control"
                  value={formData.year}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Make *</label>
                <input
                  type="text"
                  name="make"
                  className="form-control"
                  value={formData.make}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Model *</label>
                <input
                  type="text"
                  name="model"
                  className="form-control"
                  value={formData.model}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Trim</label>
                <input
                  type="text"
                  name="trim"
                  className="form-control"
                  value={formData.trim}
                  onChange={handleFormChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Mileage</label>
                <input
                  type="number"
                  name="mileage"
                  className="form-control"
                  value={formData.mileage}
                  onChange={handleFormChange}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <input
                  type="text"
                  name="color"
                  className="form-control"
                  value={formData.color}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Transmission</label>
                <select
                  name="transmission"
                  className="form-control"
                  value={formData.transmission}
                  onChange={handleFormChange}
                >
                  <option value="">Select</option>
                  <option value="Automatic">Automatic</option>
                  <option value="Manual">Manual</option>
                  <option value="CVT">CVT</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fuel Type</label>
                <select
                  name="fuelType"
                  className="form-control"
                  value={formData.fuelType}
                  onChange={handleFormChange}
                >
                  <option value="">Select</option>
                  <option value="Gasoline">Gasoline</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Electric">Electric</option>
                </select>
              </div>
            </div>

            <div className="form-buttons">
              <button onClick={() => setStep(1)} className="btn btn-secondary">
                Back
              </button>
              <button onClick={() => setStep(3)} className="btn btn-primary">
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Photos & Condition */}
        {step === 3 && (
          <div className="card form-step">
            <h2>Photos & Condition</h2>

            <div className="form-group">
              <label className="form-label">Upload Photos</label>
              <div className="photo-upload">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                  id="photo-input"
                />
                <label htmlFor="photo-input" className="upload-label">
                  {uploading ? 'Uploading...' : '+ Upload Photos'}
                </label>
              </div>

              {photos.length > 0 && (
                <div className="photos-grid">
                  {photos.map((photo, index) => (
                    <div key={index} className="photo-item">
                      <img src={photo} alt={`Photo ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(index)}
                        className="remove-photo"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group mt-3">
              <label className="form-label">Condition Assessment</label>

              <div className="condition-item">
                <div className="condition-header">
                  <label>Exterior Condition</label>
                  <span className="condition-value">{condition.exterior}/10</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  name="exterior"
                  className="form-range"
                  value={condition.exterior}
                  onChange={handleConditionChange}
                />
              </div>

              <div className="condition-item">
                <div className="condition-header">
                  <label>Interior Condition</label>
                  <span className="condition-value">{condition.interior}/10</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  name="interior"
                  className="form-range"
                  value={condition.interior}
                  onChange={handleConditionChange}
                />
              </div>

              <div className="condition-item">
                <div className="condition-header">
                  <label>Mechanical Condition</label>
                  <span className="condition-value">{condition.mechanical}/10</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  name="mechanical"
                  className="form-range"
                  value={condition.mechanical}
                  onChange={handleConditionChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes" className="form-label">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  className="form-control"
                  rows="4"
                  value={condition.notes}
                  onChange={handleConditionChange}
                  placeholder="Describe any damage, repairs, or special features..."
                />
              </div>
            </div>

            <div className="form-buttons">
              <button onClick={() => setStep(2)} className="btn btn-secondary">
                Back
              </button>
              <button onClick={() => setStep(4)} className="btn btn-primary">
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Auction Details */}
        {step === 4 && (
          <div className="card form-step">
            <h2>Auction Details</h2>

            <div className="form-group">
              <label htmlFor="title" className="form-label">Auction Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                className="form-control"
                value={auctionDetails.title}
                onChange={handleAuctionChange}
                placeholder="E.g. 2020 Honda Civic - Excellent Condition"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">Description</label>
              <textarea
                id="description"
                name="description"
                className="form-control"
                rows="4"
                value={auctionDetails.description}
                onChange={handleAuctionChange}
                placeholder="Additional details about the vehicle..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reservePrice" className="form-label">Reserve Price * ($)</label>
                <input
                  type="number"
                  id="reservePrice"
                  name="reservePrice"
                  className="form-control"
                  value={auctionDetails.reservePrice}
                  onChange={handleAuctionChange}
                  placeholder="0.00"
                  step="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="duration" className="form-label">Duration (days)</label>
                <select
                  id="duration"
                  name="duration"
                  className="form-control"
                  value={auctionDetails.duration}
                  onChange={handleAuctionChange}
                >
                  <option value="1">1 day</option>
                  <option value="3">3 days</option>
                  <option value="5">5 days</option>
                  <option value="7">7 days</option>
                  <option value="10">10 days</option>
                  <option value="14">14 days</option>
                </select>
              </div>
            </div>

            <div className="form-buttons">
              <button onClick={() => setStep(3)} className="btn btn-secondary">
                Back
              </button>
              <button
                onClick={handleCreateAuction}
                disabled={loading}
                className="btn btn-success"
              >
                {loading ? 'Creating Auction...' : 'Create Auction'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListVehicle;
